// Address — wallet-address rendering primitive. Two variants:
//   - `full`     : the entire hex inside a wallet-bar (signing surface).
//                  Optional connection dot + label ("Hardware wallet").
//   - `elided`   : `0xXXXX…XXXX` (4 hex head + 4 hex tail, matching WalletPill / bundle chip).
//
// The Phase 1.2 WalletPill is the dark-topbar chip variant. Address
// is the lighter on-card / on-bar rendering used in subscribe panels.
// Both share the elision helper at _lib/elide.

import { elideAddress } from './_lib/elide';
import { ConnectionDot } from './ConnectionDot';

export type AddressVariant = 'full' | 'elided';

export type AddressProps = {
  value: `0x${string}`;
  variant?: AddressVariant;
  status?: 'connected' | 'disconnected';
  label?: string;
};

export function Address({
  value,
  variant = 'elided',
  status,
  label,
}: AddressProps) {
  if (variant === 'elided') {
    return (
      <code className="font-mono text-[12px] text-foreground whitespace-nowrap">
        {elideAddress(value)}
      </code>
    );
  }
  return (
    <span className="inline-flex items-center gap-3 px-4 py-3 rounded-card border border-border bg-card">
      {status ? (
        <ConnectionDot status={status === 'connected' ? 'connected' : 'offline'} />
      ) : null}
      <span className="flex flex-col">
        {label ? (
          <span className="text-xs text-foreground-mute leading-tight">{label}</span>
        ) : null}
        <code className="font-mono text-[13px] font-semibold text-foreground break-all">
          {value}
        </code>
      </span>
    </span>
  );
}
